<template>
  <div class="qw-reply-recommend-wrapper">
    <div v-if="pageLoading" class="page-state-card loading"><i class="el-icon-loading"></i><span>页面初始化中...</span></div>
    <div v-else-if="pageError" class="page-state-card error"><i class="el-icon-warning-outline"></i><span>{{ pageError }}</span></div>
    <template v-else>
      <el-card class="status-card" shadow="never">
        <div class="status-row"><div><div class="status-title">企微推荐话术</div><div class="status-meta">连接状态：{{ statusTextMap[sseStatus] || sseStatus }}</div></div>
          <el-tag size="small" :type="recommendation.status === 'SUCCESS' ? 'success' : recommendation.status === 'FAILED' ? 'danger' : 'info'">{{ recommendation.status }}</el-tag></div>
        <div class="summary-line"><span>问题 {{ recommendation.problems.length }} 个</span><span v-if="recommendation.subjectName">主体：{{ recommendation.subjectName }}</span><span v-if="recommendation.currentMainGameName">游戏：{{ recommendation.currentMainGameName }}</span></div>
        <el-input v-model="searchKeyword" maxlength="50" clearable prefix-icon="el-icon-search" placeholder="搜索问题或话术（最多50字）" @input="handleSearch" />
      </el-card>
      <el-alert v-if="recommendation.sensitive" class="sensitive-banner" type="warning" show-icon :closable="false" title="当前推荐包含敏感问题，请谨慎沟通并按现有客服规范处理。" />
      <el-card v-if="showSseDebugPanel" class="sse-debug-card" shadow="never">
        <div class="sse-debug-header">
          <div><div class="sse-debug-title">SSE 推送数据</div><div class="sse-debug-subtitle">仅测试/预发/本地展示，线上域名不渲染</div></div>
          <el-button size="mini" :disabled="!sseDebugEvents.length" @click="sseDebugEvents = []">清空</el-button>
        </div>
        <div v-if="!sseDebugEvents.length" class="sse-debug-empty">等待后端推送...</div>
        <template v-else>
          <div v-for="item in sseDebugEvents" :key="item.id" class="sse-debug-item">
            <div class="sse-debug-meta">
              <span>{{ item.receivedAt }}</span><span>event: {{ item.eventType }}</span><span :class="item.applied ? 'sse-debug-applied' : 'sse-debug-ignored'">{{ item.applied ? '已更新页面' : '未更新页面' }}</span>
            </div>
            <pre class="sse-debug-json">{{ formatDebugJson(item.payload) }}</pre>
          </div>
        </template>
      </el-card>
      <ReplyGroupList v-if="visibleRecommendation.problems.length" :groups="visibleRecommendation.problems" @copyToInput="handleCopyToInput" @expand="handleExpand" />
      <el-card v-else class="page-state-card empty-card" shadow="never">{{ emptyText }}</el-card>
    </template>
  </div>
</template>

<script>
import { getContactUserId, setContactUserId } from '@/utils/localStorage';
import { qwChatDetectHistoryApi, oauthLoginApi, getQwSignatureApi, getQwSseEnv, sortASCII, refreshRecommendationSessionApi } from '@/api/qwReplyRecommend';
import { loadWecomSdk, registerWecom, getWecomEntry, getCurrentExternalContact, sendTextToCurrentChat } from './wecom';
import { createReplyRecommendSse } from './sseClient';
import { normalizeRecommendation, extractRecommendationFromSsePayload, mergeRecommendation, limitRecommendationAnswers, filterRecommendation, exposureKey, resolveSseUserId } from './normalize';
import { createRecommendationEventQueue } from './eventQueue';
import ReplyGroupList from './components/ReplyGroupList.vue';

/** 创建页面初始化和异常恢复使用的空推荐模型。 */
const emptyRecommendation = () => ({ schemaVersion: '2.0', requestId: '', status: 'NO_HIT', messageCursor: '', generatedAt: '', sensitive: false, subjectName: '', currentMainGameName: '', errorCode: '', problems: [] });
const MAX_VISIBLE_ANSWERS = 10;
const SSE_DEBUG_HOSTS = [
  'localhost',
  '127.0.0.1',
  'worksntest.hzwxbz999.cn',
  'worksnpre.hzwxbz999.cn',
  'worksn-manage-test.1uoang.cn',
  'worksn-manage-pre.1uoang.cn',
  'worksn-manage-pre.hzlz123.cn',
  'yw-worksn-manage-test.xdi2.cn',
  'yw-worksn-manage-pre.yuwanc.com',
];
const isSseDebugHost = () => process.env.NODE_ENV === 'development'
  || SSE_DEBUG_HOSTS.includes(window.location.hostname);

export default {
  name: 'QwReplyRecommend', components: { ReplyGroupList },
  /** 初始化页面响应式状态。 */
  data() { return {
    corpid: '', agentid: 1000002, pageLoading: true, pageError: '', entry: '', contactUserId: '', externalUserId: '', sseUserId: '', accessToken: '',
    pageSessionId: '', sessionToken: '', sessionExpiresAt: 0, refreshTimer: null, refreshPromise: null, recommendation: emptyRecommendation(), searchKeyword: '', searchTimer: null,
    sseStatus: 'idle', sseClient: null, reconnectCount: 0, maxReconnectCount: 3, reconnectTimer: null, isRedirecting: false,
    sseDebugEvents: [],
    eventQueue: null, exposureKeys: new Set(), statusTextMap: { idle: '未连接', connecting: '连接中', connected: '已连接', reconnecting: '重连中', error: '连接异常' },
  }; },
  computed: {
    /** 根据当前关键字返回不改变后端排序的可见推荐。 */
    visibleRecommendation() { return filterRecommendation(this.recommendation, this.searchKeyword); },
    /** 根据失败、搜索和无命中状态生成空态文案。 */
    emptyText() { if (this.recommendation.status === 'FAILED') return `推荐失败${this.recommendation.errorCode ? `：${this.recommendation.errorCode}` : ''}`; if (this.searchKeyword) return '没有匹配的问题或话术'; return '暂无推荐话术'; },
    showSseDebugPanel() { return isSseDebugHost(); },
  },
  /** 创建事件队列、读取当前企业并启动页面初始化。 */
  mounted() {
    this.eventQueue = createRecommendationEventQueue();
    const { corpid, agentid } = this.$route.query;
    this.corpid = typeof corpid === 'string' ? corpid.trim() : '';
    if (agentid) this.agentid = agentid;
    // corpid 只能来自当前企微上下文/静默授权，缺失时禁止使用固定企业兜底，避免串主体数据。
    if (!this.corpid) { this.pageError = '缺少当前企微 corpid，已阻止历史检测和 SSE 建连'; this.pageLoading = false; return; }
    this.initPage();
  },
  /** 页面销毁前关闭连接、定时器并尝试刷新埋点队列。 */
  beforeDestroy() {
    this.closeSse(); if (this.reconnectTimer) clearTimeout(this.reconnectTimer); if (this.refreshTimer) clearTimeout(this.refreshTimer); if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.eventQueue) { this.eventQueue.flush(); this.eventQueue.destroy(); }
  },
  methods: {
    /** 按客服授权、SDK、玩家、历史推荐和 SSE 的顺序初始化页面。 */
    async initPage() {
      try {
        // 初始化顺序不可交换：先确认客服身份，再注册 JS-SDK、取当前玩家，最后获取推荐和 SSE token。
        if (!await this.initContactAuth()) return;
        await loadWecomSdk();
        await registerWecom({ corpId: this.corpid, agentId: this.agentid, getAgentConfigSignature: async url => {
          const res = await getQwSignatureApi({ url, corpid: this.corpid, agentid: this.agentid }); const value = res.content; return { timestamp: value.timestamp, nonceStr: value.nonce_str, signature: value.signature };
        } });
        this.entry = await getWecomEntry(); this.externalUserId = await getCurrentExternalContact();
        if (!this.externalUserId) throw new Error('未获取到当前客户标识');
        await this.loadDetectHistory(); this.startSse();
      } catch (error) {
        console.error('[qwReplyRecommend] initPage error:', error); this.pageError = (error && (error.errMsg || error.message)) || String(error || '页面初始化失败');
        this.enqueueEvent('FAILED', { errorCode: 'PAGE_INIT_FAILED' });
      } finally { if (!this.isRedirecting) this.pageLoading = false; }
    },
    /** 从企业隔离缓存或企微静默授权获取当前客服身份。 */
    async initContactAuth() {
      // 客服缓存按 corpid 隔离，同一浏览器切换企业时不能复用其它企业的 contact_userid。
      const cached = getContactUserId(this.corpid); if (cached) { this.contactUserId = cached; return true; }
      const { code } = this.$route.query;
      if (code) {
        const res = await oauthLoginApi({ code, corpid: this.corpid, agentid: this.agentid }); const userId = res && res.content ? res.content.contact_userid : '';
        if (!userId) throw new Error('企微授权未返回客服身份'); this.contactUserId = userId; setContactUserId(userId, this.corpid);
        const query = { ...this.$route.query }; delete query.code; delete query.state; this.$router.replace({ query }); return true;
      }
      const currentUrl = window.location.href.split('?')[0]; const params = new URLSearchParams(); params.set('corpid', this.corpid); params.set('agentid', this.agentid);
      const redirectUri = encodeURIComponent(`${currentUrl}?${params.toString()}`); this.isRedirecting = true;
      window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${this.corpid}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&agentid=${this.agentid}&state=qwReplyRecommend#wechat_redirect`; return false;
    },
    /** 加载首屏历史推荐及埋点/SSE 会话凭证。 */
    async loadDetectHistory() {
      const res = await qwChatDetectHistoryApi({ corpid: this.corpid, external_userid: this.externalUserId, contact_userid: this.contactUserId });
      const content = res && res.content ? res.content : {};
      // sseUserId 只用于 Socket 路由；玩家资料、推荐请求和埋点继续使用原 externalUserId。
      this.sseUserId = resolveSseUserId(content, this.externalUserId);
      this.accessToken = content.access_token || content.accessToken || ''; this.pageSessionId = content.page_session_id || content.pageSessionId || '';
      this.sessionToken = content.session_token || content.sessionToken || ''; this.sessionExpiresAt = Number(content.session_expires_at || content.sessionExpiresAt || 0);
      if (!this.accessToken) throw new Error('历史消息检测未返回 access_token，无法建立 SSE');
      if (!this.sessionToken || !this.pageSessionId) throw new Error('历史消息检测未返回埋点会话凭证');
      this.scheduleSessionRefresh();
      this.applyRecommendation(normalizeRecommendation(content), true);
    },
    scheduleSessionRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      if (!this.sessionExpiresAt) return;
      const refreshAt = this.sessionExpiresAt * 1000 - Date.now() - 5 * 60 * 1000;
      this.refreshTimer = setTimeout(() => this.refreshSessionToken(true), Math.max(refreshAt, 1000));
    },
    async refreshSessionToken(force = false) {
      if (!this.sessionToken) return false;
      if (!force && this.sessionExpiresAt && this.sessionExpiresAt * 1000 - Date.now() > 5 * 60 * 1000) return true;
      if (this.refreshPromise) return this.refreshPromise;
      const oldToken = this.sessionToken;
      this.refreshPromise = refreshRecommendationSessionApi({ sessionToken: oldToken }).then(res => {
        const content = res && res.content ? res.content : {};
        const nextToken = content.session_token || content.sessionToken || '';
        const nextExpiresAt = Number(content.session_expires_at || content.sessionExpiresAt || 0);
        if (!nextToken || !nextExpiresAt) throw new Error('refresh recommendation session failed');
        this.sessionToken = nextToken; this.sessionExpiresAt = nextExpiresAt;
        if (content.page_session_id || content.pageSessionId) this.pageSessionId = content.page_session_id || content.pageSessionId;
        if (this.eventQueue) this.eventQueue.updateSessionToken(oldToken, nextToken);
        this.scheduleSessionRefresh();
        return true;
      }).catch(async error => {
        console.warn('[qwReplyRecommend] refresh recommendation session failed, reload detect history', error);
        try {
          await this.loadDetectHistory();
          if (this.eventQueue) this.eventQueue.updateSessionToken(oldToken, this.sessionToken);
          return true;
        } catch (reloadError) {
          this.refreshTimer = setTimeout(() => this.refreshSessionToken(true), 60 * 1000);
          throw reloadError;
        }
      }).finally(() => { this.refreshPromise = null; });
      return this.refreshPromise;
    },
    /** 写入首屏推荐或追加实时推荐，并触发新增可见话术曝光。 */
    applyRecommendation(next, force) {
      // 首屏历史结果直接写入；实时 SSE 结果追加到现有列表，相同话术去重并把新命中排前。
      const recommendation = force
        ? limitRecommendationAnswers(next || emptyRecommendation(), MAX_VISIBLE_ANSWERS)
        : mergeRecommendation(this.recommendation, next, MAX_VISIBLE_ANSWERS);
      if (!recommendation || recommendation === this.recommendation) return false;
      this.recommendation = recommendation; this.reportInitialExposures();
      if (this.recommendation.status === 'FAILED') this.enqueueEvent('FAILED', { requestId: this.recommendation.requestId, errorCode: this.recommendation.errorCode || 'RECOMMEND_FAILED' });
      return true;
    },
    /** 为当前精确会话建立 SSE，并接收完整结果替换消息。 */
    startSse() {
      if (!this.accessToken || !this.contactUserId || !this.sseUserId || !this.corpid) return;
      this.closeSse(); this.sseStatus = 'connecting';
      // appkey 继续沿用现网“客服ID_corpid”；userId 使用后端签发 token 时返回的 SSE 路由标识。
      this.sseClient = createReplyRecommendSse({ baseUrl: getQwSseEnv(), params: sortASCII({ sendMode: 'sse', type: 'qw', userId: this.sseUserId, appkey: `${this.contactUserId}_${this.corpid}`, token: this.accessToken, connectType: 1 }),
        onOpen: () => { this.sseStatus = 'connected'; this.reconnectCount = 0; },
        onMessage: (payload, eventType) => {
          const recommendation = extractRecommendationFromSsePayload(payload);
          const applied = recommendation ? this.applyRecommendation(recommendation, false) : false;
          this.recordSseDebugEvent(payload, eventType, applied);
        },
        onError: () => { this.sseStatus = 'error'; this.enqueueEvent('FAILED', { requestId: this.recommendation.requestId, errorCode: 'SSE_ERROR' }); this.reconnectSse(); },
      });
    },
    /** 记录最近 SSE 原始载荷，仅在非线上调试面板展示。 */
    recordSseDebugEvent(payload, eventType, applied) {
      if (!this.showSseDebugPanel) return;
      this.sseDebugEvents.unshift({
        id: `${Date.now()}-${Math.random()}`,
        receivedAt: new Date().toLocaleString(),
        eventType: eventType || 'message',
        applied,
        payload,
      });
      this.sseDebugEvents = this.sseDebugEvents.slice(0, 10);
    },
    formatDebugJson(value) {
      try {
        return JSON.stringify(value, null, 2);
      } catch (_) {
        return String(value);
      }
    },
    /** 在最大次数内递增延迟重建 SSE。 */
    reconnectSse() { if (this.reconnectCount >= this.maxReconnectCount) return; if (this.reconnectTimer) clearTimeout(this.reconnectTimer); this.reconnectCount += 1; this.sseStatus = 'reconnecting'; this.reconnectTimer = setTimeout(() => this.startSse(), 1500 * this.reconnectCount); },
    /** 关闭当前 SSE 客户端并清理引用。 */
    closeSse() { if (this.sseClient) { this.sseClient.close(); this.sseClient = null; } },
    /** 防抖上报最长50字的本地搜索行为。 */
    handleSearch() { if (this.searchTimer) clearTimeout(this.searchTimer); this.searchTimer = setTimeout(() => { const keyword = this.searchKeyword.trim().slice(0, 50); if (keyword) this.enqueueEvent('SEARCH', { requestId: this.recommendation.requestId, searchKeyword: keyword }); }, 500); },
    // 每个问题默认只展示第一条，因此首屏只上报第一条；展开后再上报其余实际可见话术。
    /** 上报每个问题默认可见的第一条话术曝光。 */
    reportInitialExposures() { this.recommendation.problems.forEach(problem => { const first = problem.answers[0]; if (first) this.reportExposure(problem, first); }); },
    /** 展开卡片后上报其余实际可见话术。 */
    handleExpand(problem) { problem.answers.slice(1).forEach(answer => this.reportExposure(problem, answer)); },
    /** 按请求、事件、问题和话术进行页面级曝光去重。 */
    reportExposure(problem, answer) {
      const requestId = answer.requestId || problem.requestId || this.recommendation.requestId;
      const type = problem.sensitive ? 'SENSITIVE_EXPOSURE' : 'EXPOSURE'; const key = exposureKey(requestId, type, problem.problemId, answer.answerId);
      if (this.exposureKeys.has(key)) return; this.exposureKeys.add(key);
      this.enqueueEvent(type, { requestId, problemId: problem.problemId, answerId: answer.answerId });
    },
    /** 将事件与当前页面会话令牌一起加入持久化队列。 */
    enqueueEvent(eventType, fields) {
      if (!this.eventQueue || !this.sessionToken) return;
      this.refreshSessionToken(false).catch(error => {
        console.warn('[qwReplyRecommend] enqueue with current session token after refresh failed', error);
      }).finally(() => {
        if (this.eventQueue && this.sessionToken) this.eventQueue.enqueue(this.sessionToken, Object.assign({ eventType }, fields || {}));
      });
    },
    /** 调用企微 sendChatMessage 写入当前单聊输入框并上报结果。 */
    async handleCopyToInput(answer, problem) {
      this.updateAnswerStatus(answer.clientId, 'sending');
      const requestId = answer.requestId || problem.requestId || this.recommendation.requestId;
      try {
        // “复制”必须使用企微 sendChatMessage 写入当前单聊输入框，不退化为系统剪贴板。
        if (this.entry !== 'single_chat_tools') throw new Error('当前入口不支持输入到聊天框');
        await sendTextToCurrentChat(answer.value); this.updateAnswerStatus(answer.clientId, 'sent');
        this.enqueueEvent('COPY', { requestId, problemId: problem.problemId, answerId: answer.answerId }); this.$message.success('已输入到聊天框');
      } catch (error) { this.updateAnswerStatus(answer.clientId, 'failed'); this.enqueueEvent('FAILED', { requestId, problemId: problem.problemId, answerId: answer.answerId, errorCode: 'SEND_CHAT_MESSAGE_FAILED' }); this.$message.error(error && error.message ? error.message : '输入失败'); }
    },
    /** 不可变更新指定话术的发送状态。 */
    updateAnswerStatus(clientId, status) { this.recommendation = Object.assign({}, this.recommendation, { problems: this.recommendation.problems.map(problem => Object.assign({}, problem, { answers: problem.answers.map(answer => answer.clientId === clientId ? Object.assign({}, answer, { status }) : answer) })) }); },
  },
};
</script>

<style lang="scss" scoped>
.qw-reply-recommend-wrapper { height: 100%; min-height: 100%; max-height: 100vh; overflow-y: auto; overflow-x: hidden; -webkit-overflow-scrolling: touch; padding: 15px; padding-bottom: calc(15px + env(safe-area-inset-bottom, 0px)); background: #f5f7fa; box-sizing: border-box; }
.status-card { margin-bottom: 12px; border-radius: 8px; }
.status-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.status-title { font-size: 18px; font-weight: 600; color: #303133; }
.status-meta, .summary-line { margin-top: 8px; font-size: 13px; color: #606266; word-break: break-all; }
.summary-line { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
.sensitive-banner { margin-bottom: 12px; }
.sse-debug-card { margin-bottom: 12px; border-radius: 8px; border: 1px dashed #e6a23c; }
.sse-debug-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.sse-debug-title { font-size: 15px; font-weight: 600; color: #303133; }
.sse-debug-subtitle, .sse-debug-empty, .sse-debug-meta { margin-top: 4px; font-size: 12px; color: #909399; }
.sse-debug-item { padding: 8px 0; border-top: 1px solid #ebeef5; }
.sse-debug-meta { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 6px; }
.sse-debug-applied { color: #67c23a; }
.sse-debug-ignored { color: #e6a23c; }
.sse-debug-json { max-height: 220px; overflow: auto; margin: 0; padding: 8px; background: #1f2933; color: #e6edf3; border-radius: 4px; font-size: 12px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; user-select: text; }
.page-state-card { display: flex; align-items: center; gap: 8px; padding: 16px; margin-bottom: 12px; background: #fff; border-radius: 8px; color: #606266; }
.error { color: #f56c6c; }.empty-card,.loading { justify-content: center; }
</style>
